import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useSession } from 'next-auth/react';
import { AppConfig } from '@config';
import { pattern } from '@endurancewarranty/lib';
import { AgentStatusEnum, SoftphoneConnectionBehaviorEnum, SoftphoneMessageEventEnum } from '@lib/enums';
import { useJotaiAtom } from '@states/jotai/hooks';
import { softphoneAtom } from '@states/softphone/softphoneAtom';
import type { TSoftphoneMessage } from '@lib/types/TSoftphoneMessage';
import type { TAgentStateEvent } from '@models/contacts/sessions/events/TAgentStateEvent';
import type { TAgentSessionEvents } from '@models/contacts/sessions/TAgentSessionEvents';
const {
  match
} = pattern;
const IS_SOFTPHONE_ENABLED = AppConfig.softphoneIntegration.enabled;
const ENABLE_DEBUG_LOGS = AppConfig.softphoneIntegration.enableDebugLogs;
type TUseSoftphoneWorkerArgs = Readonly<{
  agentStatus: AgentStatusEnum;
  isEnabled: boolean;
}>;
type TUseSoftphoneWorker = Readonly<{
  session: Nullable<TAgentSessionEvents>;
}>;

/**
 * Handles message events passed from the worker thread
 * and updates the softphoneAtom accordingly.
 */
export function useSoftphoneWorker(args: TUseSoftphoneWorkerArgs): TUseSoftphoneWorker {
  const [{
    connectionBehavior,
    shouldRestartWorker
  }, setSoftphoneState] = useJotaiAtom(softphoneAtom);
  const [session, setSession] = useState<Nullable<TAgentSessionEvents>>(null);
  const {
    data: userSession
  } = useSession();
  const accessTokenRef = useRef(userSession?.accessToken);
  const workerRef = useRef<Nullable<Worker>>(null);
  useEffect(() => {
    accessTokenRef.current = userSession?.accessToken;
  }, [userSession?.accessToken]);
  const shouldConnectToSoftphone = connectionBehavior === SoftphoneConnectionBehaviorEnum.AutoConnect || connectionBehavior === SoftphoneConnectionBehaviorEnum.OptIn;
  const terminateWorker = useCallback(() => {
    if (workerRef.current != null) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);
  const handleMessageFromWorker = useCallback((message: TSoftphoneMessage, worker: Worker): void => {
    debug(`=> ${message.eventType}`, 'WORKER');
    const confirmEvent = async (agentStatus: Maybe<AgentStatusEnum>): Promise<void> => {
      try {
        // Session may already be lost (e.g. after failed token refresh).
        // Posting a null token to the worker wastes retries.
        const token = accessTokenRef.current;
        if (token == null) {
          console.error('Softphone: no access token available, skipping event confirmation');
          return;
        }
        worker.postMessage({
          eventType: SoftphoneMessageEventEnum.ConfirmEvent,
          payload: {
            agentStatus,
            token
          }
        });
      } catch (error) {
        const message = 'Failed to request next softphone event';
        console.error(message);
        Sentry.addBreadcrumb({
          level: 'error',
          message,
          data: error as Error
        });
        Sentry.captureException(error);
      }
    };
    match(message).with({
      eventType: SoftphoneMessageEventEnum.DestroyWorker
    }, () => {
      terminateWorker();
      setSoftphoneState(currentState => ({
        ...currentState,
        agentStatus: AgentStatusEnum.Disconnected,
        isOptedIn: false,
        sessionId: null,
        moxyCustomerNumber: null
      }));
    }).with({
      eventType: SoftphoneMessageEventEnum.NotFound
    }, async () => {
      await confirmEvent(args.agentStatus);
    }).with({
      eventType: SoftphoneMessageEventEnum.SendEvent
    }, async message => {
      setSession(message.payload.session);
      const agentState = message?.payload?.session?.events[0] as Maybe<TAgentStateEvent>;
      const agentStatus = agentState?.currentState;
      await confirmEvent(agentStatus);
    }).otherwise(message => console.error(`Unhandled softphone message event ${message.eventType}`));
  }, [args.agentStatus, setSoftphoneState, terminateWorker]);
  const startWorker = useCallback(async () => {
    if (typeof Worker !== 'undefined' && IS_SOFTPHONE_ENABLED && args.isEnabled && shouldConnectToSoftphone) {
      terminateWorker();
      try {
        const worker = new Worker(new URL('@/workers/softphone', import.meta.url));
        workerRef.current = worker;
        const token = accessTokenRef.current;
        setSoftphoneState(currentState => ({
          ...currentState,
          agentStatus: AgentStatusEnum.Connecting,
          shouldRestartWorker: false
        }));
        worker.onmessage = event => {
          handleMessageFromWorker(event.data as TSoftphoneMessage, worker);
        };
        worker.postMessage({
          eventType: SoftphoneMessageEventEnum.StartListener,
          payload: {
            token,
            agentStatus: args.agentStatus
          }
        });
      } catch (error) {
        console.error('Softphone could not initialize. Please refresh and try again.', error);
      }
    }
  }, [args.agentStatus, args.isEnabled, handleMessageFromWorker, setSoftphoneState, shouldConnectToSoftphone, terminateWorker]);
  useEffect(() => {
    startWorker();
    return terminateWorker;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (shouldRestartWorker) {
      startWorker();
    }
  }, [shouldRestartWorker, startWorker]);
  return {
    session
  };
}
function debug(message: unknown, tag?: string): void {
  Sentry.addBreadcrumb({
    level: 'debug',
    message: message as string
  });
  if (ENABLE_DEBUG_LOGS) {
    console.log(`[SOFTPHONE]${tag != null ? `[${tag.toUpperCase()}] ${message}` : ` ${message}`}`);
  }
}